import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import type {
  UiStepProgressOrientation,
  UiStepProgressSize,
  UiStepProgressStatus,
  UiStepProgressStep,
  UiStepProgressVariant,
} from './step-progress.types';

export type {
  UiStepProgressOrientation,
  UiStepProgressSize,
  UiStepProgressStatus,
  UiStepProgressStep,
  UiStepProgressVariant,
} from './step-progress.types';

/**
 * Organismo: indicador de progreso por pasos.
 *
 * Presenta una secuencia ordenada de pasos con estados visuales y conectores.
 * Es solo presentacional: no contiene Router, HTTP ni reglas de negocio.
 */
@Component({
  selector: 'ui-step-progress',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  templateUrl: './step-progress.component.html',
  styleUrl: './step-progress.component.scss',
  host: {
    class: 'ui-step-progress-host',
    '[attr.data-orientation]': 'orientation()',
    '[attr.data-size]': 'size()',
    '[attr.data-variant]': 'variant()',
  },
})
export class UiStepProgressComponent {
  readonly steps = input.required<readonly UiStepProgressStep[]>();
  readonly orientation = input<UiStepProgressOrientation>('horizontal');
  readonly variant = input<UiStepProgressVariant>('full');
  readonly size = input<UiStepProgressSize>('md');
  readonly ariaLabel = input('Progreso del proceso');
  readonly showStepNumber = input(true);
  readonly showStatusLabel = input(true);
  readonly interactive = input(false);

  readonly stepSelect = output<UiStepProgressStep>();

  protected readonly hasSteps = computed(() => this.steps().length > 0);

  protected readonly totalSteps = computed(() => this.steps().length);

  /** Índice del paso a destacar en la variante compacta. */
  protected readonly activeIndex = computed(() => {
    const steps = this.steps();
    const current = steps.findIndex((step) => step.status === 'current');
    if (current >= 0) {
      return current;
    }
    if (steps.length > 0 && steps.every((step) => step.status === 'completed')) {
      return steps.length - 1;
    }
    return 0;
  });

  protected readonly activeStep = computed<UiStepProgressStep | null>(
    () => this.steps()[this.activeIndex()] ?? null,
  );

  /** Porcentaje de avance (paso actual / total) para la barra compacta. */
  protected readonly progressPercent = computed(() => {
    const total = this.totalSteps();
    if (total <= 0) {
      return 0;
    }
    return Math.round(((this.activeIndex() + 1) / total) * 100);
  });

  protected statusLabel(status: UiStepProgressStatus): string {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'current':
        return 'En progreso';
      case 'error':
        return 'Requiere atencion';
      case 'pending':
      default:
        return 'Pendiente';
    }
  }

  protected stepAriaLabel(step: UiStepProgressStep, index: number): string {
    return (
      step.ariaLabel ??
      `Paso ${index + 1}: ${step.label}. ${this.statusLabel(step.status)}`
    );
  }

  protected connectorState(index: number): UiStepProgressStatus {
    const current = this.steps()[index];
    const next = this.steps()[index + 1];

    if (current?.status === 'error' || next?.status === 'error') {
      return 'error';
    }

    if (current?.status === 'completed' && next?.status === 'completed') {
      return 'completed';
    }

    if (current?.status === 'completed' && next?.status === 'current') {
      return 'current';
    }

    return 'pending';
  }

  protected isCurrent(step: UiStepProgressStep): boolean {
    return step.status === 'current';
  }

  protected onStepSelect(step: UiStepProgressStep): void {
    if (!this.interactive()) {
      return;
    }

    this.stepSelect.emit(step);
  }
}
